// eslint-disable-next-line @typescript-eslint/naming-convention
import React from 'react';

import { useGetAllGroupsMutation } from '../features/group/groupApi';
import { selectedOrg } from '../features/orgManagement/orgSlice';
import { useTypedSelector } from '../app/store';

type ReturnProps = {
    isLoading: boolean;
    loadingNode: any;
    fetchHierachy: any;
    handleCleanNode: any;
    groupsData: any;
    hierarchyMappedData: any;
    hasChild: (args: any) => boolean;
    filters: any;
};

export type DeviceHierarchyProps = {
    hierarchyType?: 'device' | 'group' | 'both';
    action?: 'disable' | 'disableCheck' | 'hidden';
    disableOrg?: boolean;
    hideOrg?: boolean;
    selectionType?: 'checkbox' | 'radio' | 'node';
    customSelectedNodes?: any;
    customHandleTreeSelection?: any;
    deviceTypeId?: string;
    selectorKey?: string;
};

export const useHandleDeviceHierarchy = ({ hierarchyType, hideOrg, action }: DeviceHierarchyProps): ReturnProps => {
    const [loadingNode, setLoadingNode] = React.useState([]);
    const [groupsData, setGroupsData] = React.useState<any>();
    const selectedDeviceType = useTypedSelector((state) => state.scheduleMobile.createSchedule.deviceTypeId);

    const [hierarchyMappedData, setHierarchyMappedData] = React.useState({});
    const currentOrg = useTypedSelector(selectedOrg);
    const groupSelected = useTypedSelector((state) => state.scheduleMobile.groupSelected);

    const [getAllGroups, { data }]: any = useGetAllGroupsMutation({
        fixedCacheKey: 'cachedAllGroupData',
    });

    React.useEffect(() => {
        if (!groupsData?.length) {
            setGroupsData(data?.data?.records);
        }
    }, [data]);

    React.useEffect(() => {
        if (!currentOrg?.defaultGroupAccess || hideOrg) {
            void getAllGroups({});
        }
    }, [currentOrg, hideOrg]);

    const hasChild = (treeItem: any): boolean =>
        hierarchyType === 'group' && action === 'hidden'
            ? treeItem?.groupCount
            : treeItem?.deviceCount || treeItem?.groupCount;

    const handleCleanNode = (parentId: string): void => {
        setHierarchyMappedData((prevState: any) => {
            const newHierarchyMappingData: any = { ...prevState };
            delete newHierarchyMappingData[parentId];
            return newHierarchyMappingData;
        });
    };

    const fetchHierachy = async (currentNode: any): Promise<void> => {
        setLoadingNode((prevState): any => [...prevState, currentNode?.id]);
        const { data: childHierarchyData }: any = await getAllGroups({ parent: currentNode?.id });
        if (childHierarchyData?.success) {
            setHierarchyMappedData((prev: any) => ({
                ...prev,
                [currentNode?.id]: childHierarchyData.data?.records ?? [],
            }));
        }

        setLoadingNode((prevState): any => prevState.filter((node: any) => node !== currentNode?.id));
    };

    const handleFilter = (node: any): any => {
        const isDeviceSelectable = !groupSelected;
        switch (node?.type) {
            case 'GROUP':
                if (isDeviceSelectable) {
                    return [{ action: 'disableCheck' }];
                }
                break;
            case 'GATEWAY':
                if (isDeviceSelectable) {
                    return [{ action: 'disableCheck' }];
                }
                if (!isDeviceSelectable) {
                    return [{ action: 'hidden' }];
                }
                break;

            case 'DEVICE':
                if (
                    (node?.deviceTypeId && node?.deviceTypeId !== selectedDeviceType && isDeviceSelectable) ||
                    !isDeviceSelectable
                ) {
                    return [{ action: 'hidden' }];
                }
                break;

            default:
                if (node?.id === currentOrg?.id) {
                    return [{ action: 'disableCheck' }];
                }
                break;
        }
    };

    return {
        hasChild,
        isLoading: currentOrg?.defaultGroupAccess && !hideOrg ? false : !groupsData,
        loadingNode,
        fetchHierachy,
        handleCleanNode,
        groupsData:
            currentOrg?.defaultGroupAccess && !hideOrg
                ? [
                      {
                          id: currentOrg?.id,
                          name: currentOrg?.name,
                          deviceCount: currentOrg?.deviceCount,
                          groupCount: currentOrg?.groupCount,
                          type: 'GROUP',
                      },
                  ]
                : groupsData,
        hierarchyMappedData,
        filters: handleFilter,
    };
};
